import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  useRef,
  useCallback,
  useEffect,
} from 'react';
import { message } from 'antd';
import { format as formatDate } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { BATCH_JOBS_DASHBOARD as trans } from '../containers/Dashboard/StatusMonitoring/BatchJobs/translationValues';
import { getUser, getUserRole } from '../utils/services/userAPI';
import { submitBatch } from '../utils/services/hybridMailAPI';
import moment from 'moment';

const BatchJobContext = createContext();

const NAMESPACE = ['BATCH_JOBS_DASHBOARD'];

export const BatchJobProvider = ({ children, onReturn, onBatchCreated, setCurrentPage,setFilterValues,setFilters,setRequiresMyApproval, ...props }) => {
  const { draftBatchInfo: { batchId: draftBatchId, batchName, batchType, channelType, stageNumber, communicationId, approved,
    processingType, scheduledTime, requiresApproval, approvalType: draftApprovalType, approver } = {} } = props;
  const { t } = useTranslation(NAMESPACE);
  const fileInputRef = useRef(null);
  const [currentStep, setCurrentStep] = useState(stageNumber ? stageNumber + 1 : 1);
  const [jobName, setJobName] = useState(batchName || '');
  const [format, setFormat] = useState(
    channelType ? (channelType === 'multiChannel' ? 'MULTI CHANNEL' : channelType.toUpperCase()) : 'PRINT'
  );
  const [jobType, setJobType] = useState(batchType || 'STANDARD');
  const [scheduleEnabled, setScheduleEnabled] = useState(processingType === 'SCHEDULED');
  const [selectedTemplate, setSelectedTemplate] = useState(communicationId || null);
  const [searchQuery, setSearchQuery] = useState('');
  const [uploadedFile, setUploadedFile] = useState(null);
  const [scheduleDate, setScheduleDate] = useState(scheduledTime ? scheduledTime.split(" ")[0] : "");
  const [scheduleTime, setScheduleTime] = useState(
    scheduledTime ? scheduledTime.split(" ")[1] : ""
  );
  const [showDownloadDropdown, setShowDownloadDropdown] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const [formData, setFormData] = useState({});
  const [languageSearchQuery, setLanguageSearchQuery] = useState('');
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const [toEmails, setToEmails] = useState([]);
  const [ccEmails, setCcEmails] = useState([]);
  const [toEmailInput, setToEmailInput] = useState('');
  const [ccEmailInput, setCcEmailInput] = useState('');
  const [toPhoneNumbers, setToPhoneNumbers] = useState([]);
  const [toPhoneNumberInput, setToPhoneNumberInput] = useState('');
  const [proofsApproved, setProofsApproved] = useState(approved || false);
  const [approvalEnabled, setApprovalEnabled] = useState(requiresApproval === true);
  const [approvalType, setApprovalType] = useState(draftApprovalType ? draftApprovalType : 'LEVEL');
  const [approvalLevel, setApprovalLevel] = useState(draftApprovalType === "LEVEL" && approver ? approver : null);
  const [selectedApprover, setSelectedApprover] = useState(draftApprovalType === "USER" && approver ? approver : null);
  const [fetchedTemplates, setFetchedTemplates] = useState([]);
  const [page, setPage] = useState(0);
  const [dataCount, setDataCount] = useState(0);
  const [filteredLanguages, setFilteredLanguages] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [requestData, setRequestData] = useState();
  const [envValue, setEnvValue] = useState();
  const [selectedTemplateId,setSelectedTemplateId] = useState(communicationId || null);
  const [batchId, setBatchId] = useState(draftBatchId || null);
  const [proffDataList, setProffDataList] = useState();
  const [approvers, setApprovers] = useState([]);
  const [totalProofCount, setTotalProofCount] = useState({ print: 0, email: 0 });
  const [isHybridSubmitting, setIsHybridSubmitting] = useState(false);
  const [autoTrigger, setAutotrigger] = useState(true);

  const initialFetchRef = useRef(true);

  const steps = useMemo(() => {
    if (jobType === 'ADVANCED') {
      return [
        {
          number: 1,
          title: t(trans.STEP_BATCH_INFO_TITLE.key, trans.STEP_BATCH_INFO_TITLE.value),
          description: t(trans.STEP_BATCH_INFO_DESC.key, trans.STEP_BATCH_INFO_DESC.value),
        },
        {
          number: 2,
          title: t(trans.STEP_TEMPLATE_SEL_TITLE.key, trans.STEP_TEMPLATE_SEL_TITLE.value),
          description: t(trans.STEP_TEMPLATE_SEL_DESC.key, trans.STEP_TEMPLATE_SEL_DESC.value),
        },
        {
          number: 3,
          title: t(trans.STEP_DATA_UPLOAD_TITLE.key, trans.STEP_DATA_UPLOAD_TITLE.value),
          description: t(trans.STEP_DATA_UPLOAD_DESC.key, trans.STEP_DATA_UPLOAD_DESC.value),
        },
        {
          number: 4,
          title: t(trans.STEP_PROOF_BATCH_TITLE.key, trans.STEP_PROOF_BATCH_TITLE.value),
          description: t(trans.STEP_PROOF_BATCH_DESC.key, trans.STEP_PROOF_BATCH_DESC.value),
        },
        {
          number: 5,
          title: t(trans.STEP_SCHEDULE_TITLE.key, trans.STEP_SCHEDULE_TITLE.value),
          description: t(trans.STEP_SCHEDULE_DESC.key, trans.STEP_SCHEDULE_DESC.value),
        },
      ];
    } else if (jobType === 'FORM') {
      return [
        {
          number: 1,
          title: t(trans.STEP_BATCH_INFO_TITLE.key, trans.STEP_BATCH_INFO_TITLE.value),
          description: t(trans.STEP_BATCH_INFO_DESC.key, trans.STEP_BATCH_INFO_DESC.value),
        },
        {
          number: 2,
          title: t(trans.STEP_LANG_TEMPLATE_TITLE.key, trans.STEP_LANG_TEMPLATE_TITLE.value),
          description: t(trans.STEP_LANG_TEMPLATE_DESC.key, trans.STEP_LANG_TEMPLATE_DESC.value),
        },
        {
          number: 3,
          title: t(trans.STEP_FILL_FORM_TITLE.key, trans.STEP_FILL_FORM_TITLE.value),
          description: t(trans.STEP_FILL_FORM_DESC.key, trans.STEP_FILL_FORM_DESC.value),
        },
      ];
    } else {
      return [
        {
          number: 1,
          title: t(trans.STEP_BATCH_INFO_TITLE.key, trans.STEP_BATCH_INFO_TITLE.value),
          description: t(trans.STEP_BATCH_INFO_DESC.key, trans.STEP_BATCH_INFO_DESC.value),
        },
        {
          number: 2,
          title: t(trans.STEP_TEMPLATE_SEL_TITLE.key, trans.STEP_TEMPLATE_SEL_TITLE.value),
          description: t(trans.STEP_TEMPLATE_SEL_DESC.key, trans.STEP_TEMPLATE_SEL_DESC.value),
        },
        {
          number: 3,
          title: t(trans.STEP_DATA_UPLOAD_TITLE.key, trans.STEP_DATA_UPLOAD_TITLE.value),
          description: t(trans.STEP_DATA_UPLOAD_DESC.key, trans.STEP_DATA_UPLOAD_DESC.value),
        },
      ];
    }
  }, [jobType, t]);

  const totalSteps = steps.length;

  const canProceed = () => {
    if (currentStep === 1)
      return jobType === 'FORM' ? format !== '' : jobName.trim() !== '' && format !== '';
    if (currentStep === 2)
      return jobType === 'FORM'
        ? selectedTemplate !== null && selectedLanguage
        : selectedTemplate !== null;
    if (currentStep === 3 && jobType === 'FORM') {
      if (format === 'EMAIL') {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(toEmailInput.trim());
      }
      if (format === 'SMS' || format === 'CALL') {
        return /^[0-9+]+$/.test(toPhoneNumberInput.trim());
      }
      return true;
    }
    if (currentStep === 3) return uploadedFile !== null;
    if (currentStep === 4 && jobType === 'ADVANCED') return proofsApproved;
    if (currentStep === 5 && jobType === 'ADVANCED') return (scheduleEnabled && (scheduleDate === '' || scheduleTime === '')) || (approvalEnabled && ((approvalType === 'USER' || approvalType === 'LEVEL') && (selectedApprover === null && approvalLevel === null))) ? false : true;
    return false;
  };

  const handleAddToEmail = email => {
    const trimmedEmail = email.trim();
    if (
      trimmedEmail &&
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail) &&
      !toEmails.includes(trimmedEmail)
    ) {
      setToEmails([...toEmails, trimmedEmail]);
      setToEmailInput('');
    }
  };

  const handleRemoveToEmail = emailToRemove =>
    setToEmails(toEmails.filter(email => email !== emailToRemove));

  const handleAddCcEmail = email => {
    const trimmedEmail = email.trim();
    if (
      trimmedEmail &&
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail) &&
      !ccEmails.includes(trimmedEmail)
    ) {
      setCcEmails([...ccEmails, trimmedEmail]);
      setCcEmailInput('');
    }
  };

  const handleRemoveCcEmail = emailToRemove =>
    setCcEmails(ccEmails.filter(email => email !== emailToRemove));

  const handleAddToPhoneNumber = phone => {
    const trimmedPhone = phone.trim();
    if (
      trimmedPhone &&
      /^[\d\s\-\+\(\)]+$/.test(trimmedPhone) &&
      !toPhoneNumbers.includes(trimmedPhone)
    ) {
      setToPhoneNumbers([...toPhoneNumbers, trimmedPhone]);
      setToPhoneNumberInput('');
    }
  };

  const handleRemoveToPhoneNumber = phoneToRemove =>
    setToPhoneNumbers(toPhoneNumbers.filter(phone => phone !== phoneToRemove));

  const handleNext = () => {
    if (currentStep < totalSteps && canProceed()) setCurrentStep(currentStep + 1);
  };

  const handleBack = () => {
    if(currentStep === 4) return;
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const handleFileSelect = e => {
    if (e.target.files && e.target.files[0]) setUploadedFile(e.target.files[0]);
  };

  const handleDownloadSample = fmt => {
    let sampleContent = '';
    let filename = '';
    let mimeType = '';
    if (fmt === 'Excel') {
      filename = 'sample_data.xlsx';
      sampleContent =
        'Name,Email,Phone\nJohn Doe,john@example.com,(555) 123-4567\nJane Smith,jane@example.com,(555) 987-6543';
      mimeType = 'application/vnd.ms-excel';
    } else if (fmt === 'CSV') {
      filename = 'sample_data.csv';
      sampleContent =
        'Name,Email,Phone\nJohn Doe,john@example.com,(555) 123-4567\nJane Smith,jane@example.com,(555) 987-6543';
      mimeType = 'text/csv';
    } else if (fmt === 'JSON') {
      filename = 'sample_data.json';
      sampleContent = JSON.stringify(
        [{ name: 'John Doe', email: 'john@example.com', phone: '(555) 123-4567' }],
        null,
        2,
      );
      mimeType = 'application/json';
    } else if (fmt === 'XML') {
      filename = 'sample_data.xml';
      sampleContent =
        '<?xml version="1.0" encoding="UTF-8"?>\n<records>\n  <record>\n    <name>John Doe</name>\n  </record>\n</records>';
      mimeType = 'application/xml';
    } else if (fmt === 'Text') {
      filename = 'sample_data.txt';
      sampleContent = 'Name\tEmail\tPhone\nJohn Doe\tjohn@example.com\t(555) 123-4567';
      mimeType = 'text/plain';
    }
    const blob = new Blob([sampleContent], { type: mimeType });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    setShowDownloadDropdown(false);
  };

  const handleSubmit = () => {
    const templateName = null;
    if (jobType === 'FORM') {
      let toastMessage = '';
      if (format === 'PRINT')
        toastMessage = t(trans.MSG_DOC_DOWNLOADING.key, trans.MSG_DOC_DOWNLOADING.value);
      else if (format === 'EMAIL')
        toastMessage = t(trans.MSG_EMAIL_SENT.key, trans.MSG_EMAIL_SENT.value);
      else if (format === 'SMS') toastMessage = t(trans.MSG_SMS_SENT.key, trans.MSG_SMS_SENT.value);
      message.success(toastMessage, { duration: 5000 });
      setTimeout(() => {
        if (onReturn) onReturn();
        else if (setCurrentPage) setCurrentPage('dashboard');
      }, 5000);
      return;
    }
    let successMessage = '';
    if (approvalEnabled) {
      const approverName =
        approvalType === 'level'
          ? t(trans.LEVEL_APPROVER_NAME.key, {
              level: approvalLevel,
              defaultValue: trans.LEVEL_APPROVER_NAME.value,
            })
          : approvers.find(a => a.id === selectedApprover)?.name ||
            t(trans.THE_APPROVER.key, trans.THE_APPROVER.value);
      successMessage = t(trans.MSG_JOB_SUBMITTED_APPROVAL.key, {
        approverName: approverName,
        defaultValue: trans.MSG_JOB_SUBMITTED_APPROVAL.value,
      });
    } else if (scheduleEnabled && scheduleDate && scheduleTime) {
      const formattedDate = formatDate(scheduleDate, 'MMM dd, yyyy');
      successMessage = t(trans.MSG_JOB_SCHEDULED_DATE.key, {
        date: formattedDate,
        time: scheduleTime,
        defaultValue: trans.MSG_JOB_SCHEDULED_DATE.value,
      });
    } else {
      successMessage = t(trans.MSG_JOB_SCHEDULED.key, trans.MSG_JOB_SCHEDULED.value);
    }
    message.success(successMessage, { duration: 5000 });
    setTimeout(() => {
      onBatchCreated({
        jobName,
        format,
        jobType,
        selectedTemplate: templateName,
        uploadedFileName: uploadedFile ? uploadedFile.name : null,
        scheduleDate: scheduleDate ? formatDate(scheduleDate, 'do MMM yyyy') : undefined,
        scheduleTime: scheduleTime || undefined,
        requiresApproval: approvalEnabled,
        approver:
          approvalType === 'user' && selectedApprover
            ? approvers.find(a => a.id === selectedApprover)?.name
            : approvalEnabled
            ? `Level ${approvalLevel} Approver`
            : undefined,
        language: selectedLanguage,
        formData,
        toEmails,
        ccEmails,
        toPhoneNumbers,
        approvalLevel: approvalEnabled ? approvalLevel : undefined,
        approvalType: approvalEnabled ? approvalType : undefined,
      });
      if (onReturn) onReturn();
      else if (setCurrentPage) setCurrentPage('dashboard');
    }, 5000);
  };

  const handleHybridSubmit = useCallback(
    async (batchId, cb) => {
      setIsHybridSubmitting(true);
      const isScheduled = scheduleEnabled === true;

      let scheduledTime = null;

      if (isScheduled && scheduleDate && scheduleTime) {
        const utcDateTime = moment(`${scheduleDate} ${scheduleTime}`, "YYYY-MM-DD HH:mm:ss")
          .utc()
          .format("YYYY-MM-DD HH:mm:ss");
        scheduledTime = utcDateTime;
      }

      const approvalRequired = approvalEnabled === true;

      let approver = null;

      if (approvalRequired) {
        if (approvalType === 'USER') {
          approver = selectedApprover || null;
        } else if (approvalType === 'LEVEL') {
          approver = approvalLevel ? String(approvalLevel) : null;
        }
      }

      if (scheduleEnabled) {
        if (!scheduleDate || !scheduleTime) {
          setIsHybridSubmitting(false);
          message.error(t(trans.SCHEDULE_DATE_TIME_REQUIRED.key, trans.SCHEDULE_DATE_TIME_REQUIRED.value));
          return;
        }
      }

      if (approvalEnabled && approvalType === 'USER') {
        if (!selectedApprover) {
          setIsHybridSubmitting(false);
          message.error(t(trans.APPROVER_REQUIRED.key, trans.APPROVER_REQUIRED.value));
          return;
        }
      }

      if (approvalEnabled && approvalType === 'LEVEL') {
        if (!approvalLevel) {
          setIsHybridSubmitting(false);
          message.error(t(trans.LEVEL_REQUIRED.key, trans.LEVEL_REQUIRED.value));
          return;
        }
      }

      const payload = {
        batchId: batchId,
        processingType: isScheduled ? 'SCHEDULED' : autoTrigger ? 'IMMEDIATE' : 'MANUAL',
        scheduledTime: isScheduled ? scheduledTime : null,
        approvalRequired,
        approvalType: approvalRequired ? approvalType : null,
        approver,
      };

      try {
        const res = await submitBatch(payload);
        if (res?.statusCode === 7016) {
          message.success(t(trans.MSG_BATCH_SUBMIT_SUCCESS.key, trans.MSG_BATCH_SUBMIT_SUCCESS.value));
          cb && setTimeout(() => {
            setIsHybridSubmitting(false);
            cb();
          }, 500);
        }
        if (res?.statusCode === 7015 || res?.statusCode === 7022) {
          message.error(t(trans.MSG_BATCH_SUBMIT_FAILED.key, trans.MSG_BATCH_SUBMIT_FAILED.value));
          setIsHybridSubmitting(false);
          return;
        }
      } catch (error) {
        console.error('Submission failed:', error);
      }
    },
    [
      scheduleEnabled,
      scheduleDate,
      scheduleTime,
      approvalEnabled,
      approvalType,
      approvalLevel,
      selectedApprover,
      autoTrigger
    ],
  );

  useEffect(() => {
    let isMounted = true; // prevent state update if unmounted

    const fetchData = async () => {
      try {
        const [usersRes, rolesRes] = await Promise.all([getUser(), getUserRole()]);

        if (!usersRes?.data || !rolesRes?.data) return;

        // Create Set of approver role IDs in one pass
        const approverRoleIds = new Set();

        for (const role of rolesRes.data) {
          if (
            role.name === 'jobApproverL1' ||
            role.name === 'jobApproverL2' ||
            role.name === 'jobApproverL3' ||
            role.name === 'jobApproverL4'
          ) {
            approverRoleIds.add(role.id);
          }
        }

        // Filter users in single pass
        const filteredUsers = usersRes.data.filter(user =>
          user.roles?.some(roleId => approverRoleIds.has(roleId)),
        );

        if (isMounted) {
          setApprovers(filteredUsers);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, []);

  const value = {
    fileInputRef,
    currentStep,
    setCurrentStep,
    jobName,
    setJobName,
    format,
    setFormat,
    jobType,
    setJobType,
    scheduleEnabled,
    setScheduleEnabled,
    selectedTemplate,
    setSelectedTemplate,
    searchQuery,
    setSearchQuery,
    uploadedFile,
    setUploadedFile,
    scheduleDate,
    setScheduleDate,
    scheduleTime,
    setScheduleTime,
    showDownloadDropdown,
    setShowDownloadDropdown,
    selectedLanguage,
    setSelectedLanguage,
    formData,
    setFormData,
    languageSearchQuery,
    setLanguageSearchQuery,
    showLanguageDropdown,
    setShowLanguageDropdown,
    toEmails,
    setToEmails,
    ccEmails,
    setCcEmails,
    toEmailInput,
    setToEmailInput,
    ccEmailInput,
    setCcEmailInput,
    toPhoneNumbers,
    setToPhoneNumbers,
    toPhoneNumberInput,
    setToPhoneNumberInput,
    proofsApproved,
    setProofsApproved,
    approvalEnabled,
    setApprovalEnabled,
    approvalType,
    setApprovalType,
    approvalLevel,
    setApprovalLevel,
    selectedApprover,
    setSelectedApprover,
    filteredLanguages,
    setFilteredLanguages,
    steps,
    totalSteps,
    canProceed,
    handleAddToEmail,
    handleRemoveToEmail,
    handleAddCcEmail,
    handleRemoveCcEmail,
    handleAddToPhoneNumber,
    handleRemoveToPhoneNumber,
    handleNext,
    handleBack,
    handleFileSelect,
    handleDownloadSample,
    handleSubmit,
    setCurrentPage,
    onReturn,
    fetchedTemplates,
    setFetchedTemplates,
    page,
    setPage,
    dataCount,
    setDataCount,
    languages,
    setLanguages,
    initialFetchRef,
    requestData,
    setRequestData,
    envValue,
    setEnvValue,
    selectedTemplateId,
    setSelectedTemplateId,
    batchId,
    setBatchId,
    proffDataList,
    setProffDataList,
    handleHybridSubmit,
    approvers,
    setApprovers,
    totalProofCount,
    setTotalProofCount,
    isHybridSubmitting,
    draftBatchInfo: props.draftBatchInfo,
    setFilterValues,
    setFilters,
    setRequiresMyApproval,
    autoTrigger,
    setAutotrigger
  };

  return <BatchJobContext.Provider value={value}>{children}</BatchJobContext.Provider>;
};

export const useBatchJob = () => {
  const context = useContext(BatchJobContext);
  if (!context) throw new Error('useBatchJob must be used within a BatchJobProvider');
  return context;
};
